import { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import StoreContext from '../../Store/context';
import useApi from '../../../utils/useApi';

const CACHE_TTL_MS = 1 * 60 * 60 * 1000; // 1 hour

const ALTA_PRIORITY_ORDER = {
  fin_operacao_prejuizo: 1,
  fin_faturamento_abaixo_meta: 2,
  fin_inadimplencia_alta: 3,
  fin_retiradas_excessivas: 4,
  fin_efeito_tesoura: 5,
  fin_queda_faturamento: 6,
  est_produto_critico: 7,
  fin_cmv_fora_padrao: 8,
  fin_alavancagem_alta: 9,
  fin_eficiencia_cobranca_baixa: 10,
};

export const getTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function useDuxiaInsights(customCompanyId) {
  const { credencials } = useContext(StoreContext) || {};
  const companyId = customCompanyId || credencials?.company || credencials?.companyData?.id || 'default';

  const cacheKey = `duzz_insights_cache_${companyId}`;
  const dismissalsKey = `duzz_insights_dismissals_${companyId}`;

  const [insightsData, setInsightsData] = useState(() => {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { timestamp, data } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL_MS && data) {
          return data;
        }
      }
    } catch (e) { }
    return null;
  });

  const [dismissals, setDismissals] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(dismissalsKey) || '{}');
    } catch (e) {
      return {};
    }
  });

  const [fetchInsights, { loading }] = useApi({
    method: 'get',
    url: '/duxia/insights',
    onCompleted: (res) => {
      if (res && res.data) {
        setInsightsData(res.data);
        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({ timestamp: Date.now(), data: res.data }),
          );
        } catch (e) { }
      }
    },
  });

  const fetchInsightsRef = useRef(fetchInsights);
  fetchInsightsRef.current = fetchInsights;

  const loadInsights = useCallback((forceRefresh = false) => {
    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { timestamp, data } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_TTL_MS && data) {
            setInsightsData(data);
            return;
          }
        }
      } catch (e) { }
    }

    if (fetchInsightsRef.current) {
      fetchInsightsRef.current();
    }
  }, [cacheKey]);

  useEffect(() => {
    loadInsights(false);
  }, [loadInsights]);

  const saveDismissals = useCallback((updated) => {
    setDismissals(updated);
    try {
      localStorage.setItem(dismissalsKey, JSON.stringify(updated));
    } catch (e) { }
  }, [dismissalsKey]);

  const isInsightDismissed = useCallback((insight) => {
    const record = dismissals[insight?.id];
    if (!record) return false;

    if (insight.category === 'activation') {
      const { dismissCount = 0, lastDismissed = 0 } = record;
      const daysToHide = dismissCount >= 3 ? 30 : 7;
      const diffMs = Date.now() - lastDismissed;
      return diffMs < daysToHide * 24 * 60 * 60 * 1000;
    }

    // Health insights: hidden for today
    if (record.hiddenDate && record.hiddenDate === getTodayStr()) {
      return true;
    }

    return false;
  }, [dismissals]);

  const handleDismissActivation = useCallback((insightId, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const current = dismissals[insightId] || { dismissCount: 0 };
    const updated = {
      ...dismissals,
      [insightId]: {
        dismissCount: (current.dismissCount || 0) + 1,
        lastDismissed: Date.now(),
      },
    };
    saveDismissals(updated);
  }, [dismissals, saveDismissals]);

  const handleHideToday = useCallback((insightId, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const updated = {
      ...dismissals,
      [insightId]: {
        hiddenDate: getTodayStr(),
      },
    };
    saveDismissals(updated);
  }, [dismissals, saveDismissals]);

  const handleDismiss = useCallback((insightId, category, e) => {
    if (category === 'activation') {
      handleDismissActivation(insightId, e);
    } else {
      handleHideToday(insightId, e);
    }
  }, [handleDismissActivation, handleHideToday]);

  const rawInsights = insightsData?.insights || [];

  const eligibleInsights = useMemo(() => {
    const active = rawInsights.filter((i) => !isInsightDismissed(i));

    const isHighOrCrit = (i) => i.severity === 'Crítica' || i.severity === 'Alta';

    const alta = active
      .filter(isHighOrCrit)
      .sort((a, b) => (ALTA_PRIORITY_ORDER[a.id] || 99) - (ALTA_PRIORITY_ORDER[b.id] || 99));

    const media = active.filter((i) => i.severity === 'Média');
    const celebration = active.filter((i) => i.category === 'celebration');
    const activation = active.filter((i) => i.category === 'activation');

    let headline = null;
    let secondaries = [];

    if (alta.length > 0) {
      headline = alta[0];
      const remaining = [
        ...alta.slice(1),
        ...media,
        ...activation,
        ...celebration,
      ];
      secondaries = remaining;
    } else {
      const combined = [...media, ...activation, ...celebration];
      if (combined.length > 0) {
        headline = combined[0];
        secondaries = combined.slice(1);
      }
    }

    return {
      headline,
      secondaries,
      displayed: [headline, ...secondaries.slice(0, 2)].filter(Boolean),
      totalEligible: (headline ? 1 : 0) + secondaries.length,
      allActive: [headline, ...secondaries].filter(Boolean),
    };
  }, [rawInsights, isInsightDismissed]);

  const hasInsights = eligibleInsights.displayed.length > 0;

  return {
    insightsData,
    loading,
    loadInsights,
    handleDismiss,
    isInsightDismissed,
    eligibleInsights,
    hasInsights,
  };
}
